import { MenuOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Dropdown, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CaretDown, Gear, PowerOff, User } from '@/assets/icons';
import FocoAcredita from '@/assets/icons/foco-acredita-logo.svg?react';
import { logout } from '@/context/application.actions';
import { useApplication } from '@/context/application.provider';
import { getStoredLanguage, loadAndChangeLanguage, SupportedLanguage } from '@/services/i18n/utils';
import { colors, fonts } from '@/styles/theme';

const { Header: HeaderLayout } = Layout;

const StyledHeader = styled(HeaderLayout)`
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: ${colors.primaryBlue} !important;
  display: inline-flex !important;
  align-items: center !important;
  width: 100% !important;
  height: 60px !important;
  padding: 8px 12px !important;

  @media (min-width: 640px) {
    height: 80px !important;
    padding: 16px !important;
  }

  @media (min-width: 1024px) {
    height: 95px !important;
    padding: 15px 40px !important;
  }
`;

const StyledLogo = styled(FocoAcredita)`
  height: 40px;
  width: 80px;

  @media (min-width: 640px) {
    height: 50px;
    width: 100px;
  }

  @media (min-width: 1024px) {
    height: 68px;
    width: 138px;
  }
`;

const HeaderContentWrapper = styled.div`
  all: unset;
  font-family: ${fonts.InterRegular} !important;
  margin-left: auto;
  line-height: normal !important;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: white !important;
  font-size: 18px;
  cursor: pointer;
  margin-right: 16px;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .anticon {
    color: white !important;
  }

  svg {
    fill: white !important;
    color: white !important;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

interface HeaderProps {
  headerContent?: React.ReactNode;
  onMenuToggle?: () => void;
  showMenuButton?: boolean;
}

const Header = ({ headerContent, onMenuToggle, showMenuButton }: HeaderProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const { applicationDispatch, auth } = useApplication();
  const { isLogged } = auth;

  const { providerInfo, userInfo } = useApplication();
  const { fantasyName: providerName } = providerInfo;
  const { name: userName } = userInfo;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  const toggleDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  const handleLanguageChange = (value: SupportedLanguage) => {
    loadAndChangeLanguage(i18n, value);
    setLanguageDropdownOpen(false);
  };

  useEffect(() => {
    const storedLanguage = getStoredLanguage();
    loadAndChangeLanguage(i18n, storedLanguage);
  }, [i18n]);

  return (
    <StyledHeader>
      {showMenuButton && onMenuToggle && (
        <MenuButton onClick={onMenuToggle}>
          <MenuOutlined />
        </MenuButton>
      )}
      <StyledLogo />
      <HeaderContentWrapper>
        {headerContent}
        {isLogged && (
          <div className="flex items-center gap-2 sm:gap-4">
            <p className="small light mr-2 hidden text-white! sm:mr-6 sm:block">
              {`${t('general.common.provider')}: ${providerName || ''}`}
            </p>
            <Dropdown
              menu={{
                items: [
                  {
                    icon: <Gear className="h-4 w-4" />,
                    key: 'myProfile',
                    label: t('general.common.myProfile'),
                    onClick: () => navigate('/my-profile'),
                  },
                  {
                    icon: <PowerOff className="h-4 w-4" />,
                    key: 'logout',
                    label: t('general.common.logout'),
                    onClick: () => {
                      applicationDispatch(logout());
                    },
                  },
                ],
              }}
              onOpenChange={setDropdownOpen}
            >
              <div className="inline-flex cursor-pointer items-center gap-1 sm:gap-2">
                <User className="[&>path]:fill-white!" />
                <p className="small light hidden text-white! sm:block">{userName || ''}</p>
                <CaretDown className={`[&>path]:fill-white! ${dropdownOpen ? 'rotate-180' : ''}`} />
              </div>
            </Dropdown>
          </div>
        )}
      </HeaderContentWrapper>
      <Dropdown
        menu={{
          items: [
            { key: 'es_es', label: 'Español' },
            { key: 'en_us', label: 'English' },
            { key: 'pt_br', label: 'Português' },
          ],
          onClick: (e) => {
            handleLanguageChange(e.key as SupportedLanguage);
          },
        }}
        onOpenChange={toggleDropdown}
      >
        <div className="ml-2 inline-flex cursor-pointer items-center gap-1 sm:ml-4 sm:gap-1">
          <div className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-white! sm:h-8 sm:w-8">
            <p className="tiny bold text-black!">{i18n.language.slice(0, 2)}</p>
          </div>
          <CaretDown
            className={`h-3 w-3 sm:h-4 sm:w-4 [&>path]:fill-white! ${languageDropdownOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </Dropdown>
    </StyledHeader>
  );
};

export default Header;
